import axios from 'axios'
import { Message } from 'element-ui'
import { getUserInfo } from '@/utils/auth'
import { getApiUrl } from '@/utils/env'

// token存储键名
const TOKEN_KEY = 'token'
const REFRESH_TOKEN_KEY = 'refreshToken'

// 创建axios实例
const http = axios.create({
  baseURL: getApiUrl(), // 使用环境变量配置的API地址
  timeout: 15000, // 请求超时时间
  headers: {
    'Content-Type': 'application/json'
  }
})

// 存储等待刷新token的请求队列
let isRefreshing = false
let requests = []

// 请求拦截器
http.interceptors.request.use(
  config => {
    // 在发送请求之前做些什么
    // 从本地存储获取token
    const token = localStorage.getItem(TOKEN_KEY)
    
    // 如果有token则添加到请求头
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }
    
    /**
     * 自动添加当前登录用户ID到每个请求
     * 提供多种方式供后端选择：
     * 1. 请求头: X-User-Id
     * 2. POST/PUT/PATCH请求体: currentUserId字段
     */
    const userInfo = getUserInfo()
    
    // 尝试从多个可能的字段获取用户ID
    let currentUserId = null
    if (userInfo) {
      currentUserId = userInfo.userId || userInfo.id || userInfo.user_id
    }
    
    if (currentUserId) {
      // 方式1: 添加到请求头（推荐方式，所有请求都包含）
      config.headers['X-User-Id'] = currentUserId
      
      // 方式2: 如果是POST/PUT/PATCH请求，且请求体是对象，自动添加currentUserId字段
      if (['post', 'put', 'patch'].includes(config.method) && 
          config.data && 
          typeof config.data === 'object' && 
          !Array.isArray(config.data) &&
          !Object.prototype.hasOwnProperty.call(config.data, 'currentUserId')) {
        config.data.currentUserId = currentUserId
      }
    }
    
    // 记录请求开始时间，用于监控请求耗时
    config.metadata = { startTime: new Date() }
    
    return config
  },
  error => {
    // 对请求错误做些什么
    console.error('请求错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
http.interceptors.response.use(
  response => {
    // 直接返回原始响应，不自动处理success状态
    return response
  },
  async error => {
    console.error('响应错误:', error)
    
    const originalRequest = error.config
    
    // 处理401错误（未授权），尝试刷新token
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // 如果正在刷新token，将请求加入队列
        return new Promise((resolve) => {
          requests.push((token) => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`
            resolve(http(originalRequest))
          })
        })
      }
      
      originalRequest._retry = true
      isRefreshing = true
      
      try {
        // 尝试使用refreshToken获取新token
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
        if (!refreshToken) {
          // 没有刷新令牌，直接跳转到登录页
          handleLogout()
          return Promise.reject(error)
        }
        
        // 发送刷新token请求
        const response = await axios.post(`${http.defaults.baseURL}/auth/refresh`, {
          refresh_token: refreshToken
        })
        
        if (response.data && response.data.success) {
          // 保存新token
          const newToken = response.data.data.token
          localStorage.setItem(TOKEN_KEY, newToken)
          
          if (response.data.data.refresh_token) {
            localStorage.setItem(REFRESH_TOKEN_KEY, response.data.data.refresh_token)
          }
          
          // 更新所有等待的请求的Authorization头
          requests.forEach(cb => cb(newToken))
          requests = []
          
          // 重试原始请求
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`
          return http(originalRequest)
        } else {
          // 刷新token失败
          handleLogout()
          return Promise.reject(error)
        }
      } catch (refreshError) {
        // 刷新token时出错
        handleLogout()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }
    
    // 处理其他HTTP状态码错误
    let message = '连接服务器失败'
    if (error.response) {
      switch (error.response.status) {
        case 400:
          message = '请求参数错误'
          break
        case 401:
          message = '未授权，请重新登录'
          handleLogout()
          break
        case 403:
          message = '您没有权限执行此操作'
          break
        case 404:
          message = '请求的资源不存在'
          break
        case 500:
          message = '服务器内部错误'
          break
        default:
          message = `请求失败: ${error.response.status}`
      }
      
      // 如果后端返回了详细错误信息，优先使用后端的错误信息
      if (error.response.data && error.response.data.message) {
        message = error.response.data.message
      }
    } else if (error.request) {
      message = '服务器未响应，请检查网络连接'
    } else {
      message = error.message || '请求过程中发生错误'
    }
    
    // 只对特定状态码自动显示错误消息，避免与业务代码重复提示
    // 401、403、500等系统级错误自动提示，业务错误由具体页面处理
    if (error.response && [401, 403, 500].includes(error.response.status)) {
      Message({
        message: message,
        type: 'error',
        duration: 5 * 1000
      })
    }
    
    return Promise.reject(error)
  }
)

// 处理登出逻辑
function handleLogout() {
  // 清除本地存储的token
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  
  // 如果不是在登录页面，则跳转到登录页
  // 注释掉登录页面跳转部分
  /*
  if (router.currentRoute.path !== '/login') {
    router.push({
      path: '/login',
      query: { redirect: router.currentRoute.path }
    })
  }
  */
}

export default http